// frontend/src/api/userService.ts
import apiClient from './index';
import type { UserAdminView, UserCreateAdminPayload, UserUpdateAdminPayload, CountResponse } from '@/types';
import type { AxiosError } from 'axios';
import axios from 'axios';

interface FetchUsersParams {
  skip?: number;
  limit?: number;
  role?: string;
  status?: string;
  search?: string;
}

export const getUsers = async (params?: FetchUsersParams): Promise<UserAdminView[]> => {
    try {
        const response = await apiClient.get<UserAdminView[]>('/users/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching users:', error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error("An unknown error occurred while fetching users.");
    }
};

export const getUsersCount = async (): Promise<CountResponse> => {
  try {
    const response = await apiClient.get<CountResponse>('/users/count');
    return response.data;
  } catch (error) {
    console.error("Error fetching users count:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error("An unknown error occurred while fetching user count.");
  }
};

export const getUserById = async (userId: number): Promise<UserAdminView> => {
    try {
        const response = await apiClient.get<UserAdminView>(`/users/${userId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching user ${userId}:`, error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error(`An unknown error occurred while fetching user ${userId}.`);
    }
};

export const createUserByAdmin = async (userData: UserCreateAdminPayload): Promise<UserAdminView> => {
    try {
        const response = await apiClient.post<UserAdminView>('/users/', userData);
        return response.data;
    } catch (error) {
        console.error('Error creating user:', error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error("An unknown error occurred while creating a user.");
    }
};

export const updateUser = async (userId: number, userData: UserUpdateAdminPayload): Promise<UserAdminView> => {
    try {
        const response = await apiClient.put<UserAdminView>(`/users/${userId}`, userData);
        return response.data;
    } catch (error) {
        console.error(`Error updating user ${userId}:`, error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error("An unknown error occurred while updating the user.");
    }
};

export const deleteUserByAdmin = async (userId: number): Promise<void> => {
    try {
        await apiClient.delete(`/users/${userId}`);
    } catch (error) {
        console.error(`Error deleting user ${userId}:`, error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw error;
    }
};
